import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { IconEdit, IconEye, IconPrinter, IconTrash } from '../../components/layout/icons';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { useToast } from '../../lib/toast';
import { fetchNurseryChildren } from './nursery.api';
import { useNurseryChildren, useRemoveNurseryChild } from './nursery.queries';
import type { NurseryChild } from './types';

const formatListingDateTime = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return '-';
  const parts = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    hour12: true, timeZone: 'Asia/Kolkata',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('day')}-${get('month')}-${get('year')}, ${get('hour')}:${get('minute')} ${get('dayPeriod')}`;
};

const formatAddress = (child: NurseryChild) => [child.address, child.district, child.state, child.pinCode]
  .filter(Boolean)
  .join(', ') || '-';

export const NurseryPage = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [deleteTarget, setDeleteTarget] = useState<NurseryChild | null>(null);
  const nurseryQuery = useNurseryChildren(page, limit, search, sortBy, sortOrder);
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
    setPage(1);
  });
  const removeMutation = useRemoveNurseryChild();
  const { showToast } = useToast();

  useEffect(() => {
    const totalPages = nurseryQuery.data?.pagination.totalPages;
    if (totalPages !== undefined && totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [nurseryQuery.data?.pagination.totalPages, page]);

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    removeMutation.mutate(deleteTarget.id, {
      onSuccess: () => { setDeleteTarget(null); showToast('Registration deleted successfully.', 'success'); },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'Registration could not be deleted.', 'error'),
    });
  };

  const busy = nurseryQuery.isFetching || removeMutation.isPending;
  const errorMessage = nurseryQuery.error instanceof ApiError ? nurseryQuery.error.message : 'Baby registrations could not be loaded.';

  const exportToExcel = async () => {
    const rows = await fetchAllPages<NurseryChild>((exportPage, exportLimit) => fetchNurseryChildren(exportPage, exportLimit, search, sortBy, sortOrder), 100);
    await exportRowsToExcel({
      fileName: `Nursery-Registrations-${new Date().toISOString().slice(0, 10)}`,
      title: 'Nursery Baby Listing',
      subtitle: search ? `Search: "${search}"` : undefined,
      columns: [
        { header: 'SN', width: 6, align: 'right', map: (_row, index) => index + 1 },
        { header: 'Baby ID', width: 12, align: 'right', map: (row) => row.babyId },
        { header: 'Baby Of', width: 22, map: (row) => row.name.toUpperCase() },
        { header: 'Guardian Name', width: 22, map: (row) => (row.guardianName || '-').toUpperCase() },
        { header: 'Address', width: 36, map: (row) => formatAddress(row).toUpperCase() },
        { header: 'Gender', width: 10, map: (row) => row.gender.toUpperCase() },
        { header: 'Mode of Delivery', width: 18, map: (row) => (row.deliveryMode || '-').toUpperCase() },
        { header: 'DOB of Baby', width: 20, map: (row) => formatListingDateTime(row.dateOfBirth).toUpperCase() },
        { header: 'Admission Date', width: 20, map: (row) => formatListingDateTime(row.admissionDate).toUpperCase() },
        { header: 'Discharge Date', width: 20, map: (row) => formatListingDateTime(row.dischargeDate).toUpperCase() },
        { header: 'Status', width: 12, map: (row) => row.babyStatus.toUpperCase() },
      ],
      rows,
    });
  };

  return (
    <div className="page-stack nursery-page">
      <div className="page-heading nursery-heading">
        <div><p className="eyebrow">Nursery</p><h1>Baby Listing</h1><p>Register babies and manage Nursery admission details.</p></div>
        <Link className="button button-primary" to="/nursery/new"><span aria-hidden="true">+</span> New Registration</Link>
      </div>

      <section className="content-card">
        <div className="table-toolbar">
          <form className={`search-box${searchInput || search ? ' has-clear-control' : ''}`} onSubmit={applySearch} role="search">
            <span aria-hidden="true">⌕</span>
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search Baby ID, baby, guardian or district" aria-label="Search Nursery registrations" />
            {searchInput || search ? (
              <button className="search-box-clear" type="button" onClick={clearFilters} aria-label="Clear Nursery search">x</button>
            ) : null}
            <button type="submit" disabled={nurseryQuery.isFetching}>Search</button>
          </form>
          <div className="table-toolbar-actions">
            <button className="button button-secondary" type="button" onClick={clearFilters} disabled={!searchInput && !search}>Clear Filter</button>
            <span className="result-count">{nurseryQuery.data?.pagination.total ?? 0} records</span>
            <ExcelExportButton onExport={exportToExcel} disabled={!nurseryQuery.isSuccess} />
          </div>
        </div>

        {nurseryQuery.isLoading ? <LoadingState /> : null}
        {nurseryQuery.isError ? <ErrorState title="Unable to load Nursery" message={errorMessage} action={{ label: 'Try again', onClick: () => void nurseryQuery.refetch() }} /> : null}
        {nurseryQuery.isSuccess && nurseryQuery.data.data.length === 0 ? (
          <EmptyState
            title={search ? 'No matching babies' : 'No babies registered'}
            message={search ? 'Try another Baby ID, name, guardian, or district.' : 'Use New Registration to add the first Nursery record.'}
            {...(search ? { action: { label: 'Clear search', onClick: clearFilters } } : {})}
          />
        ) : null}
        {nurseryQuery.isSuccess && nurseryQuery.data.data.length > 0 ? (
          <>
            <div className="table-scroll">
              <table className="nursery-table">
                <thead><tr><SortableTh control={tableSort} column="babyId">Baby Id</SortableTh><SortableTh control={tableSort} column="name">Baby Of</SortableTh><SortableTh control={tableSort} column="guardianName">Guardian Name</SortableTh><SortableTh className="nursery-address-column" control={tableSort} column="address">Address</SortableTh><SortableTh control={tableSort} column="deliveryMode">Mode of Delivery</SortableTh><SortableTh control={tableSort} column="dateOfBirth">DOB of Baby</SortableTh><SortableTh control={tableSort} column="admissionDate">Admission Date</SortableTh><th>Action</th></tr></thead>
                <tbody>{nurseryQuery.data.data.map((child) => (
                  <tr key={child.id}>
                    <td><strong>{child.babyId}</strong></td>
                    <td>{child.name.toUpperCase()}</td>
                    <td>{(child.guardianName || '-').toUpperCase()}</td>
                    <td className="nursery-address-column">{formatAddress(child).toUpperCase()}</td>
                    <td>{(child.deliveryMode || '-').toUpperCase()}</td>
                    <td>{formatListingDateTime(child.dateOfBirth).toUpperCase()}</td>
                    <td>{formatListingDateTime(child.admissionDate).toUpperCase()}</td>
                    <td>
                      <div className="nursery-row-actions">
                        <Link className="opd-row-action-icon" to={`/nursery/${child.id}/view`} title="View" aria-label="View registration"><IconEye /></Link>
                        <Link className="opd-row-action-icon is-certificate" to={`/nursery/${child.id}/certificate`} title="Certificate" aria-label="View certificate"><IconPrinter /></Link>
                        <Link className="opd-row-action-icon is-edit" to={`/nursery/${child.id}/edit`} title="Edit" aria-label="Edit registration"><IconEdit /></Link>
                        <button className="opd-row-action-icon is-delete" type="button" title="Delete" aria-label="Delete registration" disabled={busy} onClick={() => setDeleteTarget(child)}><IconTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="pagination nursery-pagination">
              <label>Rows <select value={limit} disabled={busy} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option></select></label>
              <button className="button button-secondary" disabled={busy || page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
              <span>Page {page} of {Math.max(nurseryQuery.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={busy || page >= nurseryQuery.data.pagination.totalPages} onClick={() => setPage((value) => value + 1)}>Next</button>
            </div>
          </>
        ) : null}
      </section>
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Nursery registration?"
        description={deleteTarget ? `Baby ID ${deleteTarget.babyId} for ${deleteTarget.name} will be removed from the Baby Listing.` : ''}
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        tone="danger"
        pending={removeMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};
